import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import { useField, useFormikContext } from 'formik';
import { Grid, Icon, Popup, Button } from 'semantic-ui-react';
import defaultAvatar from '../../../assets/book_cover_icon.png'; // Default book cover image
import { isS3Path, isProperlySignedS3Url, getSignedUrlForS3Path, getSignedUrlForPath } from '../../../utils/s3PathUtils';

// Debug flag to control console logging
const DEBUG = true; // Set to true for debugging

// Debug logging function
const debugLog = (...args) => {
  if (DEBUG) {
    console.log(...args);
  }
};

// Memoize the ReadOnlyField component to prevent unnecessary re-renders
const ReadOnlyField = memo(({ error, value, ...props }) => {
  // Styling for the read-only field
  const fieldStyle = {
    width: '480px', // Set maximum width to 500px
    maxWidth: '480px', // Set maximum width to 500px
    padding: '0.67857143em 1em',
    border: error ? '1px solid #e0b4b4' : '1px solid rgba(34,36,38,.15)',
    borderRadius: '.28571429rem',
    backgroundColor: error ? '#fff6f6' : '#f9f9f9',
    color: value ? '#000' : '#767676',
    cursor: 'default',
    whiteSpace: 'nowrap',      // Prevent text from wrapping
    overflow: 'hidden',        // Hide overflow
    textOverflow: 'ellipsis',  // Show ellipsis for overflow
    ...props.style
  };

  return (
    <div style={{ marginBottom: '1em' }}>
      <div style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        maxWidth: '470px'  // Also constrain the container
      }}>
        <div
          style={fieldStyle}
          title={value} // Add title attribute to show full text on hover
        >
          {value || 'No file selected. Click or drag an image to upload.'}
        </div>
      </div>
    </div>
  );
});

const FormikImageUploadVariation = ({
  urlFieldName = 'bookCoverImageUrl',
  fileFieldName = 'bookCoverImageFile',
  flagFieldName = 'hasNewImageFile',
  label = 'Book Cover',
  disabled = false,
  resourceType = 'bookCoverProfileImage',
  ...props
}) => {

  const userProfile = useSelector((state) => state.user.profile);
  const userId = userProfile?.data?.userId || 'default-user-id';

  // Use multiple fields instead of one overloaded field
  const [urlField, /* urlMeta */, urlHelpers] = useField(urlFieldName);
  const [fileField, /* fileMeta */, fileHelpers] = useField(fileFieldName);
  const [flagField, /* flagMeta */, flagHelpers] = useField(flagFieldName);
  const formik = useFormikContext();
  const { errors, touched } = formik;

  // State for the preview image
  const [previewUrl, setPreviewUrl] = useState(null);
  // State for the file object
  const [imageFile, setImageFile] = useState(null);
  // State for the displayed file name
  const [fileName, setFileName] = useState('');
  // State for image loading
  const [imageLoading, setImageLoading] = useState(false);
  // State for error messages
  const [errorMessage, setErrorMessage] = useState('');
  // State for the signed URL
  const [signedUrl, setSignedUrl] = useState(null);

  // Check if any of our fields have errors
  const hasError = (touched[urlFieldName] && errors[urlFieldName]) ||
    (touched[fileFieldName] && errors[fileFieldName]) ||
    (touched[flagFieldName] && errors[flagFieldName]) ||
    errorMessage;

  // Cleanup function for blob URLs
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        debugLog('Revoking blob URL:', previewUrl);
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Effect to get signed URL when URL changes
  useEffect(() => {
    const fetchSignedUrl = async () => {
      // Only proceed if we have a URL and it's an S3 path that's not already signed
      if (
        urlField.value &&
        typeof urlField.value === 'string' &&
        urlField.value.trim() !== '' &&
        isS3Path(urlField.value) &&
        !isProperlySignedS3Url(urlField.value)
      ) {
        try {
          // Try to get the user ID from different sources
          console.log('Using userId for signed URL:', userId);

          // Get signed URL using the appropriate utility based on resourceType
          let signed;
          if (resourceType) {
            // Use the path registry-based utility if resourceType is provided
            signed = await getSignedUrlForPath(userId, urlField.value, resourceType);
          } else {
            // Fall back to the original utility if no resourceType
            signed = await getSignedUrlForS3Path(urlField.value, userId);
          }

          // Update state with the signed URL
          setSignedUrl(signed);
          debugLog('Got signed URL:', signed);
        } catch (error) {
          console.error('Error getting signed URL:', error);
          // Try again with the basic method if the enhanced method fails
          try {
            const signed = await getSignedUrlForS3Path(urlField.value, userId);
            setSignedUrl(signed);
            debugLog('Got signed URL (fallback):', signed);
          } catch (fallbackError) {
            console.error('Error getting signed URL (fallback):', fallbackError);
          }
        }
      }
    };

    fetchSignedUrl();
  }, [urlField.value, userId, resourceType]);

  // Initialize from field values
  useEffect(() => {
    debugLog('FormikImageUploadVariation urlField value:', urlField.value);
    debugLog('FormikImageUploadVariation fileField value:', fileField.value);
    debugLog('FormikImageUploadVariation flagField value:', flagField.value);

    // If we have a new image file (flag is true)
    if (flagField.value && fileField.value) {
      debugLog('We have a new image file');
      setImageFile(fileField.value);
      setFileName(fileField.value.name);

      // Create a preview URL for the file if we don't already have one
      if (!previewUrl && fileField.value instanceof File) {
        const newPreviewUrl = URL.createObjectURL(fileField.value);
        debugLog('Created new preview URL:', newPreviewUrl);
        setPreviewUrl(newPreviewUrl);
      }
    }
    // If we have a URL but no new file
    else if (urlField.value && typeof urlField.value === 'string' && urlField.value.trim() !== '') {
      debugLog('We have a URL but no new file:', urlField.value);

      // Extract filename from URL if possible
      try {
        const url = new URL(urlField.value);
        debugLog('Parsed URL:', url);
        const pathParts = url.pathname.split('/');
        debugLog('Path parts:', pathParts);
        const lastPart = pathParts[pathParts.length - 1];
        debugLog('Last part (filename):', lastPart);
        setFileName(lastPart || 'Image');
      } catch (e) {
        debugLog('Error parsing URL:', e);
        setFileName('Image');
      }
    }
    // If we have neither
    else {
      debugLog('No field values, resetting state');
      setPreviewUrl(null);
      setImageFile(null);
      setFileName('');
    }

    // Log the current state after the effect runs
    debugLog('Current state - previewUrl:', previewUrl);
    debugLog('Current state - imageFile:', imageFile);
    debugLog('Current state - fileName:', fileName);
  }, [urlField.value, fileField.value, flagField.value, previewUrl, imageFile, fileName]);

  // Validate file before processing
  const validateFile = useCallback((file) => {
    // Reset error message
    setErrorMessage('');

    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      setErrorMessage('Please select a valid image file (JPEG, PNG, etc.)');
      return false;
    }

    // Check file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setErrorMessage(`File is too large. Maximum size is 5MB. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
      return false;
    }

    return true;
  }, []);

  // Handle file drop/selection
  const handleImageChange = useCallback((file, url) => {
    if (file) {
      // Validate file
      if (!validateFile(file)) {
        return;
      }

      // Sanitize the filename
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9_\-\.]/g, '_');

      // Update state with the new file
      setImageFile(file);
      setPreviewUrl(url);
      setFileName(sanitizedFileName);

      // Store the file in its own field
      fileHelpers.setValue(file);

      // Set the flag to indicate we have a new image
      flagHelpers.setValue(true);

      // Keep the original URL (we'll use it if upload fails)
      // No need to update urlField here as it will be updated after upload

      // Mark fields as touched
      urlHelpers.setTouched(true);
      fileHelpers.setTouched(true);
      flagHelpers.setTouched(true);

      // Force the form to recognize it's dirty by setting a field value
      formik.setFieldValue('_formDirty', Date.now());

      // Force the form to recognize that a change has been made
      if (formik.setFieldValue) {
        formik.setFieldValue('_forceUpdate', Date.now());
      }
    }
  }, [fileHelpers, flagHelpers, urlHelpers, formik, validateFile]);

  // Clear the image and reset state
  const handleClear = useCallback(() => {
    // If there's a blob URL, revoke it
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }

    // Clear component state
    setImageFile(null);
    setPreviewUrl(null);
    setFileName('');
    setErrorMessage('');

    // Clear form fields
    fileHelpers.setValue(null);
    flagHelpers.setValue(false);
    urlHelpers.setValue('');

    // Mark fields as touched
    urlHelpers.setTouched(true);
    fileHelpers.setTouched(true);
    flagHelpers.setTouched(true);
  }, [fileHelpers, flagHelpers, urlHelpers, previewUrl]);

  // Handle file selection via click
  const handleImageClick = useCallback(() => {
    if (disabled) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      if (e.target.files && e.target.files[0]) {
        const file = e.target.files[0];
        handleImageChange(file, URL.createObjectURL(file));
      }
    };
    input.click();
  }, [handleImageChange, disabled]);

  // Handle keyboard events for accessibility
  const handleKeyDown = useCallback((e) => {
    if (disabled) return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleImageClick();
    }
  }, [handleImageClick, disabled]);

  // Memoize the displayImage calculation to prevent unnecessary re-renders
  const displayImage = useMemo(() => {
    debugLog('displayImage calculation - previewUrl:', previewUrl);
    debugLog('displayImage calculation - urlField.value:', urlField.value);
    debugLog('displayImage calculation - signedUrl:', signedUrl);
    debugLog('displayImage calculation - imageFile:', imageFile);
    debugLog('displayImage calculation - hasNewImageFile:', flagField.value);

    // If we have a new file selected (flag is true), use the preview URL
    if (flagField.value && previewUrl) {
      debugLog('Using previewUrl for newly selected file');
      return previewUrl;
    }

    // If we have a signed URL, use it
    if (signedUrl) {
      debugLog('Using signed URL for display');
      return signedUrl;
    }

    // If we have a URL field value and no new file, use it
    if (urlField.value && typeof urlField.value === 'string' && urlField.value.trim() !== '' && !flagField.value) {
      debugLog('Using urlField.value directly for display');
      return urlField.value;
    }

    // Default to the book cover icon
    debugLog('Using default book cover for display');
    return defaultAvatar;
  }, [previewUrl, urlField.value, signedUrl, imageFile, flagField.value]);

  // Add debug logging for component state
  useEffect(() => {
    if (DEBUG) {
      console.log('FormikImageUploadVariation component state:');
      console.log('- urlField.value:', urlField.value);
      console.log('- isS3Path:', urlField.value ? isS3Path(urlField.value) : false);
      console.log('- isProperlySignedS3Url:', urlField.value ? isProperlySignedS3Url(urlField.value) : false);
      console.log('- signedUrl:', signedUrl);
      console.log('- displayImage:', displayImage);
      console.log('- userId from localStorage:', localStorage.getItem('userId'));
      console.log('- resourceType:', resourceType);
    }
  }, [urlField.value, signedUrl, displayImage, resourceType]);

  // Memoize the hasValue calculation to prevent unnecessary re-renders
  const hasValue = useMemo(() => {
    debugLog('hasValue calculation - imageFile:', imageFile);
    debugLog('hasValue calculation - previewUrl:', previewUrl);
    debugLog('hasValue calculation - urlField.value:', urlField.value);
    debugLog('hasValue calculation - flagField.value:', flagField.value);

    // Check if we have a new image file
    if (flagField.value && fileField.value) {
      return true;
    }

    // Check if we have a preview URL
    if (previewUrl) {
      return true;
    }

    // Check if we have a URL field value
    if (urlField.value && typeof urlField.value === 'string' && urlField.value.trim() !== '') {
      return true;
    }

    return false;
  }, [fileField.value, flagField.value, previewUrl, urlField.value, imageFile]);

  // Determine the label for the image type
  const imageTypeLabel = useMemo(() => {
    if (resourceType === 'bookCharacterAvatarImage') {
      return 'Character Avatar';
    } else if (resourceType === 'bookCoverProfileImage') {
      return 'Book Cover';
    } else {
      return label || 'Image';
    }
  }, [resourceType, label]);

  return (
    <Grid.Row className="noTopBottomPad">
      <Grid.Column width={16}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5em' }}>
              <label style={{ display: 'block', margin: 0 }}>
                {label}
                {/* Only show errors specific to this field */}
                {hasError && (
                  <Popup
                    content={errors[urlFieldName] || errors[fileFieldName] || errors[flagFieldName] || errorMessage}
                    trigger={
                      <Icon
                        name="exclamation circle"
                        color="red"
                        style={{ marginLeft: '0.5em', cursor: 'pointer' }}
                      />
                    }
                    position="top center"
                    size="tiny"
                  />
                )}
              </label>
              {hasValue && !disabled && (
                <Button
                  size="mini"
                  compact
                  icon="remove"
                  content="Clear"
                  onClick={handleClear}
                  style={{ marginLeft: '10px', padding: '4px 8px', fontSize: '0.8em' }}
                />
              )}
            </div>
            <ReadOnlyField
              value={fileName}
              error={hasError ? (errors[urlFieldName] || errors[fileFieldName] || errors[flagFieldName] || errorMessage) : null}
              {...props}
            />
            {errorMessage && (
              <div style={{ color: 'red', marginTop: '5px', fontSize: '0.9em' }}>
                {errorMessage}
              </div>
            )}
          </div>
          <div style={{ marginLeft: '20px' }}>
            {/* Custom square image wrapper with cropping */}
            <div
              role="button"
              aria-label={`Upload ${imageTypeLabel.toLowerCase()}`}
              tabIndex={disabled ? -1 : 0}
              style={{
                width: '100px',
                height: '100px',
                overflow: 'hidden',
                borderRadius: '4px',
                position: 'relative',
                border: '1px solid rgba(34,36,38,.15)',
                backgroundColor: '#f9f9f9',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.7 : 1,
              }}
              onClick={handleImageClick}
              onKeyDown={handleKeyDown}
              onDragOver={(e) => {
                if (disabled) return;
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={(e) => {
                if (disabled) return;
                e.preventDefault();
                e.stopPropagation();
                if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                  const file = e.dataTransfer.files[0];
                  handleImageChange(file, URL.createObjectURL(file));
                }
              }}
              title={disabled ? "Image upload disabled" : `Click or drag to upload ${imageTypeLabel.toLowerCase()}`}
            >
              <img
                src={displayImage}
                alt={imageTypeLabel}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  opacity: imageLoading ? 0.5 : 1,
                  transition: 'opacity 0.3s'
                }}
                onLoad={() => {
                  debugLog('Image loaded successfully');
                  setImageLoading(false);
                }}
                onError={(e) => {
                  debugLog('Error loading image');
                  setImageLoading(false);

                  // If the URL is an S3 path and we don't have a signed URL yet, try to get one
                  if (
                    urlField.value &&
                    typeof urlField.value === 'string' &&
                    isS3Path(urlField.value) &&
                    !signedUrl
                  ) {

                    console.log('Using userId for signed URL in onError handler:', userId);

                    // Try with the enhanced method first
                    if (resourceType) {
                      getSignedUrlForPath(userId, urlField.value, resourceType)
                        .then(url => {
                          setSignedUrl(url);
                          e.target.src = url;
                        })
                        .catch((error) => {
                          console.error('Error getting signed URL with resourceType:', error);
                          // Fall back to the original method
                          getSignedUrlForS3Path(urlField.value, userId)
                            .then(url => {
                              setSignedUrl(url);
                              e.target.src = url;
                            })
                            .catch(() => {
                              e.target.src = defaultAvatar;
                            });
                        });
                    } else {
                      // Use the original method directly if no resourceType
                      getSignedUrlForS3Path(urlField.value, userId)
                        .then(url => {
                          setSignedUrl(url);
                          e.target.src = url;
                        })
                        .catch(() => {
                          e.target.src = defaultAvatar;
                        });
                    }
                  } else {
                    e.target.src = defaultAvatar;
                  }
                }}
              />
              {imageLoading && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)'
                }}>
                  <Icon name="spinner" loading />
                </div>
              )}
              <div
                style={{
                  position: 'absolute',
                  bottom: '0px',
                  left: '0',
                  right: '0',
                  textAlign: 'center',
                  fontSize: '10px',
                  color: 'white',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  padding: '2px 0',
                }}
              >
                {imageTypeLabel}
              </div>
            </div>
          </div>
        </div>
      </Grid.Column>
    </Grid.Row>
  );
};

// PropTypes validation
FormikImageUploadVariation.propTypes = {
  urlFieldName: PropTypes.string,
  fileFieldName: PropTypes.string,
  flagFieldName: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  resourceType: PropTypes.string
};

// Optimize re-renders with custom comparison function
export default memo(FormikImageUploadVariation, (prevProps, nextProps) => {
  // Only re-render if these props change
  return (
    prevProps.urlFieldName === nextProps.urlFieldName &&
    prevProps.fileFieldName === nextProps.fileFieldName &&
    prevProps.flagFieldName === nextProps.flagFieldName &&
    prevProps.label === nextProps.label &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.resourceType === nextProps.resourceType
  );
});
